import {useState, useEffect, useCallback, useMemo} from 'react';
import {useUrl} from '@shopify/hydrogen';
import {
  getFilterParamsFromHref,
  getSearchQuery,
  ShopFilterParamsType,
} from '../utils';
import {filterRanges} from '../constants';

function getDefaultParams(): ShopFilterParamsType {
  return {
    list: null,
    inStock: null,
    searchTokens: [],
    priceTags: [],
    brands: [],
    types: [],
    countries: [],
    categories: [],
    ratings: [],
    partnerStores: [],
    styles: [],
    age: {...filterRanges.age},
    abv: {...filterRanges.abv},
    inTwlShop: undefined,
  };
}

const defaultSkipKeys: Array<keyof ShopFilterParamsType> = [
  'list',
  'searchTokens',
];

interface UseShopFilterUrlEncodeType {
  filterParams: ShopFilterParamsType;
  setFilterParams: (
    filterParams:
      | ShopFilterParamsType
      | React.Dispatch<React.SetStateAction<ShopFilterParamsType>>,
  ) => void;
  resetFilterParams: () => void;
  searchQuery: string;
  resetSearchQuery: string;
  hasActiveFilters: boolean;
}

export function useShopFilterUrlEncode(): UseShopFilterUrlEncodeType {
  /**
   * Due to the effect below and the complex state object, the effect
   * was stuck in an infinite loop. To work around this we have used
   * the stringafied version of the URL as the dependency to monitor
   * in the effect.
   */
  const {href} = useUrl();

  const [filterParams, setFilterParams] = useState(
    () => getFilterParamsFromHref(href, defaultSkipKeys).filters,
  );
  const [hasActiveFilters, setHasFiltersActive] = useState(
    () => getFilterParamsFromHref(href, defaultSkipKeys).active,
  );

  const resetFilterParams = useCallback(
    () => setFilterParams(getDefaultParams()),
    [],
  );

  useEffect(() => {
    const {filters, active} = getFilterParamsFromHref(href, defaultSkipKeys);
    setFilterParams(filters);
    setHasFiltersActive(active);
  }, [href]);

  const searchQuery = useMemo(
    () =>
      getSearchQuery(href, filterParams, {
        ignoreKeys: defaultSkipKeys,
      }),
    [filterParams, href],
  );

  const resetSearchQuery = useMemo(
    () =>
      getSearchQuery(href, getDefaultParams(), {
        ignoreKeys: defaultSkipKeys,
      }),
    [href],
  );

  return {
    filterParams,
    setFilterParams,
    resetFilterParams,
    searchQuery,
    resetSearchQuery,
    hasActiveFilters,
  };
}
